import * as Sentry from '@sentry/react'
import { Row, Col, App, Segmented } from 'antd'
import { Palette, ImagePlay } from 'lucide-react'
import { SunOutlined, MoonOutlined } from '@ant-design/icons'
import { useProfileContext } from '../../context/profile'
import { useThemeContext } from '../../context/theme'
import type { ThemeMode } from '@bandos/shared'
import {
  SectionCard,
  SectionHeader,
  SectionIcon,
  SectionTitle,
  SectionDescription,
} from './styles/AccountSettingsSectionStyles'
import {
  PreferencesContent,
  HelperText,
  ThemeOption,
  ThemeLabel,
} from './styles/PreferencesSectionStyles'
import { StyleSelector } from '../StyleSelector/StyleSelector'

export function PreferencesSection() {
  const { message } = App.useApp()
  const { profile, loading, updateProfile } = useProfileContext()
  const { theme, setTheme } = useThemeContext()

  const handleStyleChange = async (styleId: string) => {
    try {
      await updateProfile({ default_image_style: styleId })
      message.success('Default image style updated')
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'settings', action: 'update_image_style' },
      })
      message.error('Failed to update image style')
    }
  }

  const handleThemeChange = async (value: string | number) => {
    const newTheme = value as ThemeMode
    setTheme(newTheme)
    try {
      await updateProfile({ theme: newTheme })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'settings', action: 'update_theme' },
      })
      // Theme is already applied locally via localStorage
    }
  }

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} lg={12}>
        <SectionCard>
          <SectionHeader>
            <SectionIcon>
              <Palette size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Appearance</SectionTitle>
              <SectionDescription>Customize how the app looks</SectionDescription>
            </div>
          </SectionHeader>
          <PreferencesContent>
            <ThemeLabel>Theme</ThemeLabel>
            <ThemeOption>
              <Segmented
                value={theme}
                onChange={handleThemeChange}
                options={[
                  {
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <SunOutlined /> Light
                      </span>
                    ),
                    value: 'light',
                  },
                  {
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MoonOutlined /> Dark
                      </span>
                    ),
                    value: 'dark',
                  },
                ]}
                block
              />
            </ThemeOption>
          </PreferencesContent>
        </SectionCard>
      </Col>
      <Col xs={24} lg={12}>
        <SectionCard>
          <SectionHeader>
            <SectionIcon>
              <ImagePlay size={20} />
            </SectionIcon>
            <div>
              <SectionTitle>Image Style</SectionTitle>
              <SectionDescription>Default style for portraits and storyboards</SectionDescription>
            </div>
          </SectionHeader>
          <PreferencesContent>
            <StyleSelector
              value={profile?.default_image_style ?? 'realistic-photo'}
              onChange={handleStyleChange}
              disabled={loading}
            />
            <HelperText>
              Projects can override this style individually from the mindmap toolbar.
            </HelperText>
          </PreferencesContent>
        </SectionCard>
      </Col>
    </Row>
  )
}
